import CtaButtonGroup, { CtaType } from "@/components/molecules/CtaButtonGroup";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import SectionHeader from "./shared/SectionHeader";

/**
 * IntegrationLogoArcSection — centered eyebrow + 2-line title + subtitle
 * + CTA pair, followed by a curved arc of circular logo chips. Matches
 * the "Plug Graphy Into Your Growth Stack" block on graphy.com's
 * marketing-tools page.
 *
 * Each logo is a circular badge with a FontAwesome brand icon (or an
 * inline SVG). Badges are positioned along a shallow arc using CSS
 * translateY per item so the row curves gently downward at the edges.
 */

export type IntegrationLogoChip = {
  name: string;
  icon?: IconDefinition;
  /** Override colour for the icon — defaults to the logo's brand color
   *  via `iconColor`. */
  iconColor?: string;
  /** Optional inline SVG path (24x24 viewBox) when no FA brand icon
   *  matches, e.g. Zoho / Zapier. */
  svg?: {
    viewBox?: string;
    content: React.ReactNode;
  };
  /** Optional background fill for the badge. Defaults to white. */
  background?: string;
};

export type IntegrationLogoArcSectionData = {
  eyebrow?: string;
  titleMuted?: string;
  titleBright?: string;
  title?: string;
  subtitle?: string;
  ctas?: CtaType[];
  chips: IntegrationLogoChip[];
  /** Optional static arc artwork. When provided, replaces the dynamic
   *  chip arc rendering. */
  image?: string;
  imageAlt?: string;
};

/** Shallow U-curve — first/last chips sit low, middle chips sit high.
 *  Percentages are applied as translateY so the arc scales with chip
 *  size. Supports up to 11 chips. */
const arcOffsets = [80, 48, 20, 4, 0, 6, 0, 4, 20, 48, 80];

const IntegrationLogoArcSection = ({
  eyebrow,
  titleMuted,
  titleBright,
  title,
  subtitle,
  ctas,
  chips,
  image,
  imageAlt,
}: IntegrationLogoArcSectionData) => {
  return (
    <section className="relative w-full overflow-hidden bg-white">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 bottom-0 z-0 h-[70%]"
        style={{
          background:
            "linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,228,234,0.5) 55%, rgba(255,210,222,0.45) 100%)",
        }}
      />
      <div className="container relative z-10 mx-auto px-4 py-10 sm:px-6 md:px-8 md:py-20 lg:px-12 lg:py-24">
        <SectionHeader
          eyebrow={eyebrow}
          titleMuted={titleMuted}
          titleBright={titleBright}
          title={title}
          subtitle={subtitle}
          ctas={ctas}
          variant="light-teal"
          align="center"
        />

        {image ? (
          <div className="relative mx-auto mt-16 w-full max-w-5xl md:mt-20">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={image}
              alt={imageAlt ?? ""}
              className="h-auto w-full select-none"
              draggable={false}
            />
          </div>
        ) : (
        <div
          className="relative mx-auto mt-16 h-[180px] w-full max-w-5xl md:mt-20 md:h-[220px]"
          aria-hidden="true"
        >
          <div className="flex h-full items-start justify-between">
            {chips.map((chip, idx) => {
              const offset =
                arcOffsets[
                  Math.min(
                    arcOffsets.length - 1,
                    Math.floor(
                      (idx * (arcOffsets.length - 1)) /
                        Math.max(1, chips.length - 1)
                    )
                  )
                ];
              return (
                <div
                  key={chip.name}
                  className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full shadow-[0_8px_24px_-10px_rgba(35,34,40,0.35)] ring-1 ring-black/[0.04] md:h-16 md:w-16"
                  style={{
                    transform: `translateY(${offset}px)`,
                    background: chip.background ?? "#ffffff",
                  }}
                  title={chip.name}
                >
                  {chip.icon ? (
                    <FontAwesomeIcon
                      icon={chip.icon}
                      style={{
                        fontSize: 26,
                        color: chip.iconColor,
                      }}
                    />
                  ) : chip.svg ? (
                    <svg
                      viewBox={chip.svg.viewBox ?? "0 0 24 24"}
                      className="h-7 w-7"
                      role="img"
                      aria-label={chip.name}
                    >
                      {chip.svg.content}
                    </svg>
                  ) : (
                    <span className="font-inter text-[13px] font-semibold text-neutralBlack">
                      {chip.name[0]}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        )}
      </div>
    </section>
  );
};

export default IntegrationLogoArcSection;
